import { Chip } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import type { ServiceDetail } from '@/api/services';
import { serviceStatusTone, serviceStatusLabel } from '../services-format';
import { DetailHeader } from '@/components/detail-header';
import { ServiceActions } from './service-actions';

/**
 * Service detail identity header — breadcrumb trail (Services › customer ›
 * service) then title row: muted `#id` + service name (semibold), the dominant
 * status StatusPill, a subtle template chip + POC chip, and lifecycle actions
 * right-aligned. Delegates to the shared `DetailHeader` component.
 */
export function ServiceDetailHeader({ detail }: { detail: ServiceDetail }) {
  const customerName = detail.company_name || detail.customer_name;

  const parent = [
    { label: 'Services', to: '/services' },
    ...(detail.customer_id && customerName
      ? [{ label: customerName, to: `/customers/${detail.customer_id}` }]
      : []),
  ];

  const meta = (
    <>
      <StatusPill tone={serviceStatusTone(detail.service_status)}>
        {serviceStatusLabel(detail.service_status)}
      </StatusPill>
      {detail.service_template ? (
        <Chip appearance="subtle" tone="neutral" size="sm">
          {detail.service_template}
        </Chip>
      ) : null}
      {detail.is_poc ? (
        <Chip appearance="outline" tone="warning" size="sm">
          POC
        </Chip>
      ) : null}
    </>
  );

  return (
    <DetailHeader
      parent={parent}
      current={detail.service_name || `#${detail.service_id}`}
      idPrefix={`#${detail.service_id}`}
      title={detail.service_name}
      meta={meta}
      actions={<ServiceActions detail={detail} />}
    />
  );
}
